import React, { useMemo } from 'react';
import { useStyles } from './Button.styles';
import ImageProjects from 'assets/grid_icon.svg?react';
import ImageTeammates from 'assets/users.svg?react';
import ImageProfile from 'assets/profile_icon.svg?react';
import ImageMyOrganization from 'assets/my_organization_icon.svg?react';
import ImageAllOrganizations from 'assets/all_organization_icon.svg?react';
import ImageLogOut from 'assets/log_out_icon.svg?react';
import FilterIcon from 'assets/filter.svg?react';
import ImageBatches from 'assets/batches_icon.svg?react';
import ImageReport from 'assets/file_text.svg?react';
import ImageNotifications from 'assets/settings.svg?react';
import MemoryIcon from '@mui/icons-material/Memory';

import { themeColors } from 'app/shared/theme/theme';
import { CircularProgress, ProgressType } from '../CircularProgress';

export interface IButton {
  variant:
    | 'primary'
    | 'secondaryDark'
    | 'secondaryLight'
    | 'pagination'
    | 'header'
    | 'headerActive'
    | 'secondaryBlack'
    | 'primaryNotFound'
    | 'primaryProfile'
    | 'view'
    | 'activeView'
    | 'primarySave'
    | 'secondaryOutlined'
    | 'secondarySave'
    | 'primaryCancel'
    | 'primaryOutlined'
    | 'primaryBig'
    | 'link'
    | 'clearAll'
    | 'filter'
    | 'filterSmall'
    | 'filterActive'
    | 'filterSmallActive'
    | 'clickHere'
    | 'secondaryCancel'
    | 'secondaryProfile';
  children?: string | React.JSX.Element;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  icon?:
    | 'projects'
    | 'teammates'
    | 'profile'
    | 'myOrganization'
    | 'allOrganizations'
    | 'allNotifications'
    | 'logOut'
    | 'filter'
    | 'batches'
    | 'reports'
    | 'aiChat';
  onHover?: (value: boolean) => void;
  type?: 'button' | 'reset' | 'submit';
  height?: number;
  isLoading?: boolean;
  className?: string;
}

export const Button = ({
  variant,
  children,
  onClick,
  disabled,
  active,
  icon,
  onHover,
  type,
  height,
  isLoading,
  className,
}: IButton) => {
  const { classes } = useStyles();

  const getIcon = useMemo(() => {
    if (icon === 'projects' && active !== undefined) {
      return (
        <ImageProjects
          stroke={active ? themeColors.mbsBlue : themeColors.greyscale1}
          className={classes.icon}
        />
      );
    } else if (icon === 'teammates' && active !== undefined) {
      return (
        <ImageTeammates
          stroke={active ? themeColors.mbsBlue : themeColors.greyscale1}
          className={classes.icon}
        />
      );
    } else if (icon === 'allNotifications' && active !== undefined) {
      return (
        <ImageNotifications
          stroke={active ? themeColors.mbsBlue : themeColors.greyscale1}
          className={classes.icon}
          height={20}
        />
      );
    } else if (icon === 'batches' && active !== undefined) {
      return (
        <ImageBatches
          stroke={active ? themeColors.mbsBlue : themeColors.greyscale1}
          className={classes.icon}
        />
      );
    } else if (icon === 'aiChat' && active !== undefined) {
      return (
        <MemoryIcon
          className={classes.icon}
          htmlColor={active ? themeColors.mbsBlue : themeColors.greyscale1}
        />
      );
    } else if (icon === 'profile') {
      return <ImageProfile className={classes.icon} />;
    } else if (icon === 'myOrganization') {
      return <ImageMyOrganization className={classes.icon} />;
    } else if (icon === 'allOrganizations') {
      return <ImageAllOrganizations className={classes.icon} />;
    } else if (icon === 'reports') {
      return (
        <ImageReport
          className={classes.icon}
          stroke={active ? themeColors.mbsBlue : themeColors.greyscale1}
          height={20}
        />
      );
    } else if (icon === 'filter' && active !== undefined) {
      return (
        <FilterIcon
          height={14}
          width={14}
          strokeWidth={0.3}
          stroke={active ? themeColors.white : themeColors.mbsBlue}
          fill={active ? themeColors.white : themeColors.mbsBlue}
        />
      );
    } else if (icon === 'logOut') {
      return <ImageLogOut className={classes.icon} />;
    }
  }, [active, classes.icon, icon]);

  return (
    <button
      type={type}
      onMouseEnter={() => {
        onHover && onHover(true);
      }}
      onMouseLeave={() => {
        onHover && onHover(false);
      }}
      onClick={onClick}
      disabled={disabled}
      className={`${classes[variant]} ${className}`}
      style={{ height: height || '' }}
    >
      {icon && getIcon}
      {isLoading ? <CircularProgress type={ProgressType.LIGHT} /> : children}
    </button>
  );
};
